import { useDebugValue, useEffect, useState } from 'react';
import core, { App, View } from '@gin-betting/core';
import { sum } from 'lodash';

import { useMemoSelector } from 'hooks';
import { CONFIG_BY_SPORTS } from 'scoreMetaConfig';
import { selectFilteredScores } from 'selectors';
import { MatchPartInfo } from 'types';

const {
    viewport: {
        selectors: { selectIsExtraSmall },
    },
    entities: {
        selectors: { selectMatchMetaValueByName },
    },
} = core;

interface Input {
    matchId: string;
    sportId: string;
    numberOfShowed: number;
    isEnded?: boolean;
}

interface Output {
    meta: View.Meta[];
    extra: View.ScoreInfo[];
    showed: View.ScoreInfo[];
    isServerKnown: boolean;
    isHomeServer: boolean;
    isAwayServer: boolean;
    winner: 'home' | 'away' | '';
    isExtendable: boolean;
    isFullView: boolean;
    toggleView: () => void;
    currentMatchPart: MatchPartInfo | null;
    scoresFullLength: number;
}

const useScore = ({
    matchId,
    sportId,
    numberOfShowed,
    isEnded,
}: Input): Output => {
    const config = CONFIG_BY_SPORTS[sportId];
    const { scores, currentMatchPart, meta } = useMemoSelector(
        selectFilteredScores,
        {
            matchId,
        }
    );
    const isExtraSmall = useMemoSelector(selectIsExtraSmall);
    const adjustedScores = isExtraSmall
        ? scores.filter(s => s.type === App.ScoreTypes.TYPE_TOTAL)
        : scores;

    const server = useMemoSelector(selectMatchMetaValueByName, {
        matchId,
        metaName: 'server',
    });

    const extra = [...adjustedScores];

    const defaultTotal: View.ScoreInfo = {
        home: '',
        away: '',
        type: App.ScoreTypes.TYPE_TOTAL,
        indexNumber: 0,
    };

    const total = extra.splice(
        extra.findIndex(i => i.type === App.ScoreTypes.TYPE_TOTAL),
        1
    )[0];

    const totalScore = total || defaultTotal;
    const showed = extra.splice(-numberOfShowed);
    showed.push(totalScore);

    const extraNumber = extra.length;
    const [isExtendable, changeIsExtendable] = useState(extraNumber > 0);
    const [isFullView, changeIsFullView] = useState(false);
    const toggleView = () => changeIsFullView(prev => !prev);

    useEffect(() => {
        changeIsExtendable(extraNumber > 0);
    }, [extraNumber]);

    const { home, away } = totalScore;

    const winner =
        isEnded && !!home && !!away && sum([+home, +away]) !== 0
            ? +home > +away
                ? 'home'
                : 'away'
            : '';

    const isHomeServer = server === 'home';
    const isAwayServer = server === 'away';

    const isServerKnown =
        !!config?.scoresOrder && (isHomeServer || isAwayServer);

    useDebugValue({ showed, extra });

    return {
        extra,
        showed,
        isServerKnown,
        isHomeServer,
        isAwayServer,
        winner,
        isExtendable,
        isFullView,
        toggleView,
        currentMatchPart,
        scoresFullLength: scores.length,
        meta,
    };
};

export default useScore;
